import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, setAuth } from "@/lib/api";
import { setActiveLearner } from "@/lib/learner";
import { Loader2, Sparkles, AlertTriangle } from "lucide-react";
import LogoMark from "@/components/Logo";

/**
 * /demo/:token  — public landing page for a magic demo link.
 * Exchanges the demo token for a learner JWT, stores auth, flips the
 * "this session is a demo" sessionStorage flag (so a banner can render
 * downstream), and redirects to the targeted course's learner page.
 */
export default function DemoEntry() {
  const { token } = useParams();
  const nav = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/share/demo/${token}`);
        if (cancelled) return;
        const user = {
          email: data.email,
          role: data.role,
          name: data.name,
          learner_id: data.learner_id,
        };
        setAuth(data.token, user);
        setActiveLearner(user);
        sessionStorage.setItem("uvc_demo_mode", "1");
        sessionStorage.setItem("uvc_demo_course_id", data.course.id);
        sessionStorage.setItem("uvc_demo_course_title", data.course.title || "");
        // Land on the targeted course's learner view
        nav(`/learn/courses/${data.course.id}`, { replace: true });
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "This demo link is no longer active.");
      }
    })();
    return () => { cancelled = true; };
  }, [token, nav]);

  return (
    <div className="min-h-screen bg-[#fafaf7] flex items-center justify-center px-4">
      <div className="text-center max-w-md" data-testid="demo-entry-card">
        <LogoMark className="mx-auto mb-8 h-10 w-auto" />
        {!error ? (
          <>
            <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[3px] text-[#E53935] mb-3">
              <Sparkles size={11} /> Demo mode
            </div>
            <h1 className="font-display text-2xl text-slate-900 mb-3">
              Opening your preview…
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              We&apos;re signing you in as a demo viewer so you can experience the full learner journey.
            </p>
            <Loader2 className="animate-spin text-[#E53935] mx-auto" size={28} />
          </>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[3px] text-amber-600 mb-3">
              <AlertTriangle size={11} /> Link inactive
            </div>
            <h1 className="font-display text-2xl text-slate-900 mb-3" data-testid="demo-entry-error-title">
              This demo link is not active
            </h1>
            <p className="text-sm text-slate-500" data-testid="demo-entry-error-msg">{error}</p>
            <p className="text-xs text-slate-400 mt-4">
              Ask the course owner to regenerate the demo link.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
